import { type ParsedQuery } from 'query-string';
import { path } from 'ramda';
import { COOKIE_KEYS, REQUESTS, RESPONSE, ROUTES } from './_constants';
import Server from './config/_server';
import Config from './config/_global';
import type {
    CollectionResponse,
    ResourceResponse,
    ElasticSearchResponse
} from '../api/types/response';
import type { User, CarType, CarModel, Device, DistributedMedium, ModuleType, Playlist, PlaylistGroup} from '../api/types/index';
import helpers from './helpers';
import queryString from 'query-string';
import { jwtDecode } from 'jwt-decode';
import { generalStore } from '../store';

type CustomHeaders = {
    'Content-Type': string;
    Authorization: string;
    Accept?: string;
};

type AcceptHeader =
    | 'application/json'
    | 'application/ld+json'
    | 'application/merge-patch+json'
    | string;

export type CustomRequestOptions = {
    accept?: AcceptHeader;
    signal?: AbortSignal;
    isLoginRequest?: boolean;
    params?: Record<string, string | number | boolean | string[]> | ParsedQuery;
    body?:
        | BodyInit
        | null
        | undefined
        | Record<string, string | string[] | number | boolean | number[]>;
};

export type CustomError = Error & {
    response?: Response;
    message: string;
};

export const refreshAuth = () => {
    const refreshToken = helpers.getCookie(COOKIE_KEYS.REFRESH_TOKEN);
    return fetch(Server.baseUrl + Server.services.refreshToken, {
        method: 'POST',
        headers: {
            'Content-Type': REQUESTS.CONTENT_TYPE.JSON
        },
        body: JSON.stringify({
            refresh_token: refreshToken
        })
    })
        .then(response => {
            if (response.status === 200) {
                return response.json();
            }
            if (response.status === 401 || response.status === 400) {
                throw new Error('Unauthorized');
            }
        })
        .then(response => {
            if (typeof response.token !== 'undefined') {
                const currentTimeEpocSec = Date.now() / 1000;
                const jwtDecodedToken = jwtDecode(response.token) as any;
                const duration = jwtDecodedToken.exp - currentTimeEpocSec;
                const hours = duration / 3600;

                helpers.setCookie(COOKIE_KEYS.TOKEN, response.token, hours);
                helpers.setCookie(COOKIE_KEYS.REFRESH_TOKEN, refreshToken, hours + 4);
            }
            return response;
        })
        .catch(e => {
            if (e.message === 'Unauthorized') {
                helpers.removeCookie(COOKIE_KEYS.REFRESH_TOKEN);
                helpers.removeCookie(COOKIE_KEYS.TOKEN);
                helpers.removeCookie(COOKIE_KEYS.ID_TOKEN);
                window.location.href = ROUTES.CONNECT;
            }

            throw e;
        });
};

// TODO: Temporary fix, for 403 after login untill we rework it into contexts
let unauthorizedReattempAmount = 0;

const authFetch = async (
    method: RequestInit['method'],
    patch = false,
    url: string,
    options: Partial<CustomRequestOptions> = {},
    raw = false
) => {
    const token = helpers.getCookie(COOKIE_KEYS.TOKEN);

    /** Re-auth request interceptor */
    if (
        !helpers.isTestEnv &&
        !helpers.isAuthenticated() &&
        !options?.isLoginRequest
    ) {
        return refreshAuth().then(() =>
            authFetch(method, patch, url, options, raw)
        );
    }

    const { body, accept, ...fetchOptions } = options;
    const headers: CustomHeaders = {
        'Content-Type': REQUESTS.CONTENT_TYPE.JSON,
        Authorization: `${REQUESTS.AUTHORIZATION_TYPE} ${token}`
    };

    if (patch) {
        headers['Content-Type'] = REQUESTS.CONTENT_TYPE.PATCH;
    }

    if (typeof accept !== 'undefined') {
        headers['Accept'] = accept;
    }

    const authOptions: RequestInit = {
        method,
        headers,
        signal: options.signal,
        ...fetchOptions
    };

    if (typeof body !== 'undefined') {
        authOptions.body = JSON.stringify(options.body);
    }

    try {
        const parametrizedUrl = options.params
            ? `${url}?${queryString.stringify(options.params, {
                  arrayFormat: 'bracket'
              })}`
            : url;

        const response = await fetch(parametrizedUrl as string, authOptions);

        if (raw) {
            return response;
        }
        // TODO FIX ME!!
        if (response.status === 401 && !helpers.isAuthenticated()) {
            if (window.location.pathname !== Config.defaultUrls.noAuthenticated) {
                try {
                    await refreshAuth();
                    return authFetch(method, false, url, options, raw);
                } catch (e) {
                    const location = {
                        pathname: Config.defaultUrls.noAuthenticated,
                        state: { from: {} }
                    };
                    return Config.history.push(location);
                }
            }
        }
        if (response.status === 401 && helpers.isAuthenticated()) {
            localStorage.setItem('errorUrl', '');
            return window.location.href = '/401';
        }

        if (response.status === 404) {
            localStorage.setItem('errorUrl', url);
            return window.location.href = '/404';
        }

        if (response.status === 403) {
            // TODO: temporary fix for 403 after login untill we rework it into contexts
            if (unauthorizedReattempAmount === 0) {
                unauthorizedReattempAmount++;
                setTimeout(() => {
                    authFetch (
                        method,
                        patch,
                        url,
                        options,
                        raw
                    );
                }, 7000);
                return;
            }

            localStorage.setItem('errorUrl', url);
            return window.location.href = '/403';
        }

        if (response.status === 422) {
            const responseJson = await response.json();
            const violationMessages = responseJson.violations.map(violation => violation.message);
             const { setAlert } = generalStore.getState();

            return setAlert({
                        message: violationMessages.join(' '),
                        type: 'warning',
                        display: true,
                        isAutoDisappearing: true
                    });
        }

        if (response.status >= 500) {
            localStorage.setItem('errorUrl', url);
            return window.location.href = '/500';
        }

        if (!response.ok) {
            const error: CustomError = new Error(
                JSON.stringify(response.statusText) ||
                    JSON.stringify(response.status)
            );
            error.response = response;
            throw error;
        }
        return response.json().catch(e => {
            return response;
        });
    } catch (error: any) {
        /**
         * From AbortController
         */
        const e = error as Error;
        if (e.name === 'AbortError') {
            throw new Error('aborted');
        }
        throw error;
    }
};

const apiUpload = async (
    url: string,
    body
) => {
    const token = helpers.getCookie(COOKIE_KEYS.TOKEN);

    /** Re-auth request interceptor */
    if (
        !helpers.isTestEnv &&
        !helpers.isAuthenticated()
    ) {
        return refreshAuth().then(() =>
            apiUpload(url, body)
        );
    }

    try {
        const response = await fetch(url as string, {
            headers: {Authorization: `${REQUESTS.AUTHORIZATION_TYPE} ${token}`},
            method: "POST",
            body 
        });

        // TODO FIX ME!!
        if (response.status === 401 && !helpers.isAuthenticated()) {
            if (window.location.pathname !== Config.defaultUrls.noAuthenticated) {
                try {
                    await refreshAuth();
                    return apiUpload(url, body);
                } catch (e) {
                    const location = {
                        pathname: Config.defaultUrls.noAuthenticated,
                        state: { from: {} }
                    };
                    return Config.history.push(location);
                }
            }
        }
        if (response.status === 401 && helpers.isAuthenticated()) {
            localStorage.setItem('errorUrl', '');
            return window.location.href = '/401';
        }

        if (response.status === 404) {
            localStorage.setItem('errorUrl', url);
            return window.location.href = '/404';
        }

        if (response.status === 403) {
            if (unauthorizedReattempAmount === 0) {
                unauthorizedReattempAmount++;
                setTimeout(() => {
                    apiUpload(url, body);
                }, 7000);
                return;
            }

            localStorage.setItem('errorUrl', url);
            return window.location.href = '/403';
        }

        if (response.status >= 500) {
            localStorage.setItem('errorUrl', url);
            return window.location.href = '/500';
        }

        if (!response.ok) {
            const error: CustomError = new Error(
                JSON.stringify(response.statusText) ||
                    JSON.stringify(response.status)
            );
            error.response = response;
            throw error;
        }
        return response.json().catch(e => {
            return response;
        });
    } catch (error: any) {
        /**
         * From AbortController
         */
        const e = error as Error;
        if (e.name === 'AbortError') {
            throw new Error('aborted');
        }
        throw error;
    }
};

export type Resource =
    | PlaylistGroup
    | Device
    | ModuleType
    | User
    | Playlist
    | DistributedMedium
    | CarModel
    | CarType;

export type ApiResponse = (
    | CollectionResponse<Resource>
    | ResourceResponse<Resource>
    | ElasticSearchResponse<Resource>
) & { code: number, title?: string; countries?: any[], token?: string };

const apiGet = <T = ApiResponse>(
    url: string,
    options?: CustomRequestOptions,
    raw?: boolean
): Promise<T> => authFetch('GET', false, url, options, raw);
const apiPut = <T = ApiResponse>(
    url: string,
    options?: CustomRequestOptions,
    raw?: boolean
): Promise<T> => authFetch('PUT', false, url, options, raw);
const apiPost = <T = ApiResponse>(
    url: string,
    options?: CustomRequestOptions,
    raw?: boolean
): Promise<T> => authFetch('POST', false, url, options, raw);
const apiDelete = <T = ApiResponse>(
    url: string,
    options?: CustomRequestOptions,
    raw?: boolean
): Promise<T> => authFetch('DELETE', false, url, options, raw);
const apiPatch = <T = ApiResponse>(
    url: string,
    options?: CustomRequestOptions,
    raw?: boolean
): Promise<T> => authFetch('PATCH', true, url, options, raw);
const apiPair = <T = ApiResponse>(
    url: string,
    options?: CustomRequestOptions,
    raw?: boolean
): Promise<Response> => authFetch('PUT', false, url, options, true);

const apiGetAllPaginatedCollections = (
    url: string,
    options: CustomRequestOptions
) =>
    new Promise((resolve, reject) => {
        const getPaginationNumber = (string: string | undefined) => {
            if (!string) {
                return 0;
            }

            const pageMatch = string.match(/[?&]page=(\d+)/);
            return pageMatch ? Number(pageMatch[1]) : 0;
        };

        const buildOptionsForPage = (page?: number): CustomRequestOptions => {
            const params = options.params ? { ...options.params } : undefined;

            if (typeof page !== 'undefined' && params) {
                params.page = page;
            }

            return {
                ...options,
                params
            };
        };

        const pump = async () => {
            try {
                const firstResponse = await apiGet(url, buildOptionsForPage());
                const collections = [
                    ...firstResponse[RESPONSE.EXPAND_COLLECTION]
                ];

                // if hydra:view does not exists in the response
                // All data fits into first page
                if (!firstResponse[RESPONSE.VIEW]) {
                    return resolve(collections);
                }

                const currentPage = getPaginationNumber(
                    path(RESPONSE.CURRENT_PAGE, firstResponse)
                );
                const lastPage = getPaginationNumber(
                    path(RESPONSE.LAST_PAGE, firstResponse)
                );

                if (currentPage >= lastPage) {
                    return resolve(collections);
                }

                const remainingPages = Array.from(
                    { length: lastPage - currentPage },
                    (_, index) => currentPage + index + 1
                );

                const remainingResponses = await Promise.all(
                    remainingPages.map(page => apiGet(url, buildOptionsForPage(page)))
                );

                const allCollections = remainingResponses.reduce(
                    (accumulator, response) => [
                        ...accumulator,
                        ...response[RESPONSE.EXPAND_COLLECTION]
                    ],
                    collections
                );

                return resolve(allCollections);
            } catch (error) {
                return reject(JSON.stringify(error));
            }
        };

        pump();
    });

export {
    apiGet,
    apiGetAllPaginatedCollections,
    apiPut,
    apiPost,
    apiDelete,
    apiPatch,
    authFetch,
    apiPair,
    apiUpload
};
